import { createDeal } from "@/api/deals-api";
import { raiseBoardApi } from "@/api/raise-board-api";
import { isIndustriesPendingApproval } from "@/lib/deal-field-state";
import { DealTransformed } from "@/lib/schema/deal-schema";
import { formatTimeAgo, resolveIndustryLabel } from "@/lib/utils";
import { isDevS3File } from "@/lib/url-utils";
import { getLastActivityDate } from "@/services/deals-service";
import { useAuthStore } from "@/store/auth-store";
import { useWorkspaceStore } from "@/store/workspace-store";
import { useDealsViewStore, type SortField } from "@/store/deals-view-store";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CheckCircle,
  Gem,
  Loader2,
  MoreVertical,
} from "lucide-react";
import { CompanyAvatar } from "./company-avatar";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/api-error";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogTrigger } from "../ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { DeleteDialog } from "./delete-dialog";
import { DeactivateDealDialog } from "./deactivate-deal-dialog";
import { QuickActionsToolbar } from "./quick-actions-toolbar";
import {
  compareDeals,
  getFitScore,
  getReportStatusIndicator,
  hasFitScore,
} from "./utils";

interface TableViewProps {
  deals: DealTransformed[];
  onStatusChange: (params: { dealId: string; status: string }) => void;
  workspaceId: string;
  onDealDeleted?: () => void;
  onDealArchived?: () => void;
}

interface CompanyFile {
  _id: string;
  file_type: string;
  file_url: string;
  file_size: string;
  time_stamp: string;
  company: string;
}

const getPitchdeckFile = (files: CompanyFile[]) => {
  return files.find(
    (file) =>
      file.file_type === "company pitchdeck" && !isDevS3File(file.file_url)
  );
};

// Sort-header arrow. Active field shows direction; inactive shows muted bidirectional.
function SortArrow({
  field,
  currentField,
  direction,
}: {
  field: SortField;
  currentField: SortField;
  direction: "asc" | "desc";
}) {
  if (field !== currentField) {
    return (
      <ArrowUpDown className="inline h-4 w-4 ml-2 text-muted-foreground/40" />
    );
  }
  return direction === "asc" ? (
    <ArrowUp className="inline h-4 w-4 ml-2" />
  ) : (
    <ArrowDown className="inline h-4 w-4 ml-2" />
  );
}

export const TableView = ({
  deals,
  onStatusChange,
  workspaceId,
  onDealDeleted,
  onDealArchived,
}: TableViewProps) => {
  const { user } = useAuthStore();
  const { activeWorkspace } = useWorkspaceStore();
  const { sort, setSort, toggleSortDirection } = useDealsViewStore();

  const [selectedDealIds, setSelectedDealIds] = useState<Set<string>>(
    new Set()
  );

  const statusPriority = useMemo(() => {
    return (activeWorkspace?.statuses || []).reduce(
      (acc: Record<string, number>, status, index: number) => ({
        ...acc,
        [status.title]: index,
      }),
      {} as Record<string, number>
    );
  }, [activeWorkspace?.statuses]);

  // Fit Score column renders only if at least one deal in view has been
  // analyzed. Gap Analysis is hidden from the table — too dense for daily
  // scanning; it's still visible on the deal detail page.
  const showFitScoreColumn = useMemo(() => deals.some(hasFitScore), [deals]);

  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean;
    dealId: string;
    dealName: string;
  }>({
    isOpen: false,
    dealId: "",
    dealName: "",
  });

  const [deactivateDialog, setDeactivateDialog] = useState<{
    isOpen: boolean;
    dealId: string;
    dealName: string;
  }>({
    isOpen: false,
    dealId: "",
    dealName: "",
  });

  const sortedDeals = useMemo(() => {
    return [...deals].sort((a, b) =>
      compareDeals(a, b, sort.field, sort.direction, { statusPriority })
    );
  }, [deals, sort, statusPriority]);

  const handleSort = (field: SortField) => {
    if (sort.field === field) {
      toggleSortDirection();
    } else {
      setSort({ field, direction: field === "name" ? "asc" : "desc" });
    }
  };

  const handleAddToRaiseBoard = async (dealId: string, dealName: string) => {
    if (!workspaceId) return;
    try {
      await raiseBoardApi.bulkSet(workspaceId, [dealId], true);
      toast.success(`Added ${dealName} to Raiseboard`);
    } catch (error) {
      console.error("Failed to add deal to Raiseboard:", error);
      toast.error(getErrorMessage(error, "Failed to add deal to Raiseboard"));
    }
  };

  const handleArchive = async (dealId: string) => {
    if (!workspaceId) return;
    try {
      await createDeal.createFlaggedCompany(
        workspaceId,
        dealId,
        user?.id ?? ""
      );
      onDealArchived?.();
      toast.success("Deal archived successfully");
    } catch (error) {
      console.error("Failed to archive deal:", error);
      toast.error(getErrorMessage(error, "Failed to archive deal"));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedDealIds(new Set(sortedDeals.map((deal) => deal.id)));
    } else {
      setSelectedDealIds(new Set());
    }
  };

  const handleSelectDeal = (dealId: string, checked: boolean) => {
    const newSelection = new Set(selectedDealIds);
    if (checked) {
      newSelection.add(dealId);
    } else {
      newSelection.delete(dealId);
    }
    setSelectedDealIds(newSelection);
  };

  const clearSelection = () => {
    setSelectedDealIds(new Set());
  };

  const isAllSelected =
    selectedDealIds.size === sortedDeals.length && sortedDeals.length > 0;
  const isIndeterminate =
    selectedDealIds.size > 0 && selectedDealIds.size < sortedDeals.length;

  return (
    <div className="h-full overflow-hidden w-full p-4">
      <div className="flex h-full flex-col rounded-md border border-border bg-card overflow-hidden">
        <div className="flex-1 overflow-auto">
      <Table className="outline-none border-none w-full" tabIndex={-1}>
        <TableHeader className="[&_tr]:border-b [&_tr]:border-border sticky top-0 z-10 bg-card">
          <TableRow className="border-b border-border [&_th]:text-[10px] [&_th]:font-semibold [&_th]:uppercase [&_th]:tracking-widest [&_th]:text-muted-foreground/80 [&_th]:whitespace-nowrap [&_th_button]:uppercase [&_th_button]:tracking-widest">
            <TableHead className="w-12">
              <div className="relative inline-flex">
                <Checkbox
                  checked={isAllSelected}
                  onCheckedChange={handleSelectAll}
                  aria-label="Select all deals"
                  className={
                    isIndeterminate
                      ? "border-foreground bg-foreground text-background shadow-none"
                      : "border-border shadow-none data-[state=checked]:bg-foreground data-[state=checked]:border-foreground data-[state=checked]:text-background"
                  }
                />
                {isIndeterminate && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="h-0.5 w-2 rounded-sm bg-background" />
                  </div>
                )}
              </div>
            </TableHead>
            <TableHead
              onClick={() => handleSort("name")}
              className="cursor-pointer hover:bg-muted"
            >
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger className="flex items-center">
                    Deal Name
                    <SortArrow
                      field="name"
                      currentField={sort.field}
                      direction={sort.direction}
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>The name of the company or investment opportunity</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </TableHead>

            {showFitScoreColumn && (
              <TableHead
                onClick={() => handleSort("fitScore")}
                className="cursor-pointer hover:bg-muted"
              >
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger className="flex items-center">
                      Fit Score
                      <SortArrow
                        field="fitScore"
                        currentField={sort.field}
                        direction={sort.direction}
                      />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        A score indicating how well the deal matches your
                        investment criteria
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </TableHead>
            )}

            <TableHead className="hidden lg:table-cell">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>Industry</TooltipTrigger>
                  <TooltipContent>
                    <p>
                      The business sectors or industries the company operates in
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </TableHead>
            <TableHead>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>Source</TooltipTrigger>
                  <TooltipContent>
                    <p>How this deal was sourced</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </TableHead>
            <TableHead
              onClick={() => handleSort("status")}
              className="cursor-pointer hover:bg-muted"
            >
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger className="flex items-center">
                    Status
                    <SortArrow
                      field="status"
                      currentField={sort.field}
                      direction={sort.direction}
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Current stage of the deal in your pipeline</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </TableHead>
            <TableHead
              onClick={() => handleSort("lastActivity")}
              className="cursor-pointer hover:bg-muted hidden lg:table-cell"
            >
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger className="flex items-center">
                    Last Activity
                    <SortArrow
                      field="lastActivity"
                      currentField={sort.field}
                      direction={sort.direction}
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      Time since the most recent update or activity on this deal
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedDeals.map((deal) => {
            const pitchdeckFile = getPitchdeckFile(deal.company_files || []);
            const statusIndicator = getReportStatusIndicator(
              deal.reportStatus?.opportunityApplication?.report_status
            );
            const statusColor = activeWorkspace?.statuses.find(
              (s) => s.title === deal.overview.status
            )?.colour;
            // Leading-dot fill from workspace status color. Per DESIGN.md
            // status-badge spec + absolute ban on side-stripe borders.
            const statusDotStyle = statusColor?.startsWith("#")
              ? { backgroundColor: statusColor }
              : undefined;

            return (
              <TableRow
                key={deal.id}
                className="group border-b border-border/40 transition-colors hover:bg-foreground/[0.02]"
              >
                <TableCell className="w-12">
                  <Checkbox
                    checked={selectedDealIds.has(deal.id)}
                    onCheckedChange={(checked) =>
                      handleSelectDeal(deal.id, checked as boolean)
                    }
                    aria-label={`Select ${deal.overview.name}`}
                    className="border-border shadow-none data-[state=checked]:bg-foreground data-[state=checked]:border-foreground data-[state=checked]:text-background"
                  />
                </TableCell>
                <TableCell className="max-w-[300px] px-6 py-2">
                  <Link
                    to={`/${workspaceId}/deals/${deal.id}`}
                    className="block"
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <CompanyAvatar
                        companyFiles={deal.company_files}
                        websiteUrl={deal.overview.website}
                        alt={deal.overview.name}
                      />
                      <span className="min-w-0">
                        <span className="flex items-center gap-1">
                          <span className="text-sm font-medium text-foreground group-hover:underline decoration-muted-foreground/30 underline-offset-2 truncate">
                            {deal.overview.name}
                          </span>
                          {deal.aiTracked && (
                            <Gem className="h-3.5 w-3.5 shrink-0 text-primary" />
                          )}
                          {deal.prospectiveAffEmail && (
                            <CheckCircle className="h-3.5 w-3.5 shrink-0 text-green-500" />
                          )}
                          {statusIndicator && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="inline-flex shrink-0">
                                    <statusIndicator.icon
                                      className={`h-3.5 w-3.5 ${
                                        statusIndicator.type === "loading"
                                          ? "text-blue-500 animate-spin"
                                          : "text-red-500"
                                      }`}
                                    />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {statusIndicator.label}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </span>
                      </span>
                    </span>
                  </Link>
                </TableCell>

                {showFitScoreColumn && (
                  <TableCell>
                    {hasFitScore(deal) ? (
                      <span className="font-mono text-sm font-medium tabular-nums text-foreground">
                        {Math.round(getFitScore(deal) as number)}
                        <span className="text-muted-foreground/50 font-normal ml-0.5">
                          /100
                        </span>
                      </span>
                    ) : statusIndicator?.type === "loading" ? (
                      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground whitespace-nowrap">
                        <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
                        Analyzing…
                      </span>
                    ) : statusIndicator?.type === "failed" ? (
                      <Link
                        to={`/${workspaceId}/deals/${deal.id}?tab=analysis`}
                        className="text-xs text-red-500 hover:underline underline-offset-2 whitespace-nowrap"
                      >
                        Analysis failed
                      </Link>
                    ) : (
                      <Link
                        to={`/${workspaceId}/deals/${deal.id}?tab=analysis`}
                        className="text-xs text-muted-foreground hover:text-foreground hover:underline underline-offset-2 whitespace-nowrap"
                      >
                        Run analysis →
                      </Link>
                    )}
                  </TableCell>
                )}

                <TableCell className="hidden lg:table-cell px-4 py-2">
                  <div className="flex flex-wrap items-center gap-1">
                    {isIndustriesPendingApproval(deal) ? (
                      <Link
                        to={`/${workspaceId}/deals/${deal.id}?tab=profile#field-industries`}
                        className="inline-flex items-center gap-1.5 rounded-sm bg-purple-50 px-1.5 py-0.5 text-xs font-medium text-purple-700 transition-colors hover:bg-purple-100 dark:bg-purple-950/40 dark:text-purple-200 dark:hover:bg-purple-900/40"
                      >
                        <span className="h-1.5 w-1.5 rounded-full bg-purple-500" />
                        Review industries
                      </Link>
                    ) : deal.overview.industry?.length > 0 ? (
                      <>
                        {(() => {
                          // Resolve GICS codes → labels, then de-dupe and
                          // drop any "—" placeholders from unknown codes so
                          // the chip row only shows real industry names.
                          const labels = Array.from(
                            new Set(
                              deal.overview.industry
                                .map(resolveIndustryLabel)
                                .filter((l) => l && l !== "—")
                            )
                          );
                          if (labels.length === 0) {
                            return (
                              <span className="text-xs text-muted-foreground/40">
                                —
                              </span>
                            );
                          }
                          return (
                            <>
                              {labels.slice(0, 2).map((label, i) => (
                                <span
                                  key={i}
                                  className="inline-flex items-center rounded-sm bg-input px-1.5 py-0.5 text-xs text-foreground"
                                >
                                  {label}
                                </span>
                              ))}
                              {labels.length > 2 && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="inline-flex items-center rounded-sm bg-input px-1.5 py-0.5 text-xs text-muted-foreground cursor-default">
                                        +{labels.length - 2}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p className="max-w-[280px]">
                                        {labels.slice(2).join(", ")}
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </>
                          );
                        })()}
                      </>
                    ) : (
                      <span className="text-xs text-muted-foreground/40">
                        —
                      </span>
                    )}
                  </div>
                </TableCell>

                <TableCell className="px-4 py-2">
                  <span className="text-xs text-muted-foreground/80">
                    {deal.overview.source || (
                      <span className="text-muted-foreground/40">—</span>
                    )}
                  </span>
                </TableCell>

                <TableCell className="px-4 py-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="inline-flex items-center gap-1.5 rounded-sm bg-input/60 px-1.5 py-1 text-[10px] font-semibold uppercase tracking-widest leading-none text-foreground transition-colors hover:bg-input"
                      >
                        <span
                          aria-hidden
                          className={
                            statusDotStyle
                              ? "h-1.5 w-1.5 rounded-full"
                              : "h-1.5 w-1.5 rounded-full bg-muted-foreground/50"
                          }
                          style={statusDotStyle}
                        />
                        {deal.overview.status}
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuRadioGroup
                        value={deal.overview.status}
                        onValueChange={(value) => {
                          onStatusChange({
                            dealId: deal.id,
                            status: value,
                          });
                        }}
                      >
                        {(activeWorkspace?.statuses || []).map((status) => {
                          const itemDotStyle = status.colour?.startsWith("#")
                            ? { backgroundColor: status.colour }
                            : undefined;
                          return (
                            <DropdownMenuRadioItem
                              key={status.title}
                              value={status.title}
                              className="pl-2 [&>span:first-child]:hidden"
                            >
                              <span
                                aria-hidden
                                className={
                                  itemDotStyle
                                    ? "mr-2 h-1.5 w-1.5 shrink-0 rounded-full"
                                    : "mr-2 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/50"
                                }
                                style={itemDotStyle}
                              />
                              {status.title}
                            </DropdownMenuRadioItem>
                          );
                        })}
                      </DropdownMenuRadioGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>

                <TableCell className="hidden lg:table-cell px-4 py-2">
                  <span className="font-mono text-xs tabular-nums text-muted-foreground/60">
                    {formatTimeAgo(getLastActivityDate(deal))}
                  </span>
                </TableCell>

                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="px-0">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {pitchdeckFile && !isDevS3File(pitchdeckFile.file_url) && (
                        <>
                          <Dialog>
                            <DialogTrigger asChild>
                              <DropdownMenuItem
                                onSelect={(e) => e.preventDefault()}
                              >
                                View Pitchdeck
                              </DropdownMenuItem>
                            </DialogTrigger>
                            <DialogContent className="max-w-4xl h-[80vh]">
                              <div className="relative w-full h-full">
                                <iframe
                                  src={`https://docs.google.com/viewer?url=${encodeURIComponent(
                                    pitchdeckFile.file_url
                                  )}&embedded=true`}
                                  className="w-full h-full"
                                  title="Pitchdeck Viewer"
                                />
                              </div>
                            </DialogContent>
                          </Dialog>
                          <DropdownMenuItem
                            onClick={() => {
                              const link = document.createElement("a");
                              link.href = pitchdeckFile.file_url;
                              link.download = "pitchdeck.pdf";
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            }}
                          >
                            Download Pitchdeck
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                        </>
                      )}
                      <DropdownMenuItem
                        onClick={() =>
                          handleAddToRaiseBoard(deal.id, deal.overview.name)
                        }
                      >
                        Add to Raiseboard
                      </DropdownMenuItem>
                      {(() => {
                        const portfolioStatus = activeWorkspace?.statuses.find(
                          (s) =>
                            s.title.toLowerCase().includes("portfolio")
                        )?.title;
                        if (
                          !portfolioStatus ||
                          deal.overview.status === portfolioStatus
                        )
                          return null;
                        return (
                          <DropdownMenuItem
                            onClick={() =>
                              onStatusChange({
                                dealId: deal.id,
                                status: portfolioStatus,
                              })
                            }
                          >
                            Add to Portfolio
                          </DropdownMenuItem>
                        );
                      })()}
                      {deal.aiTracked && (
                        <DropdownMenuItem
                          onClick={() =>
                            setDeactivateDialog({
                              isOpen: true,
                              dealId: deal.id,
                              dealName: deal.overview.name,
                            })
                          }
                        >
                          Mark as not premium
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => handleArchive(deal.id)}
                        className="text-yellow-600"
                      >
                        Archive
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() =>
                          setDeleteDialog({
                            isOpen: true,
                            dealId: deal.id,
                            dealName: deal.overview.name,
                          })
                        }
                      >
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
        </div>
      </div>

      {selectedDealIds.size > 0 && (
        <QuickActionsToolbar
          selectedDealIds={Array.from(selectedDealIds)}
          onClearSelection={clearSelection}
          onDealDeleted={onDealDeleted}
          onDealArchived={onDealArchived}
          workspaceId={workspaceId}
        />
      )}

      <DeleteDialog
        isOpen={deleteDialog.isOpen}
        onClose={() =>
          setDeleteDialog({ isOpen: false, dealId: "", dealName: "" })
        }
        dealId={deleteDialog.dealId}
        dealName={deleteDialog.dealName}
        workspaceId={workspaceId}
        onSuccess={() => {
          onDealDeleted?.();
        }}
      />

      <DeactivateDealDialog
        isOpen={deactivateDialog.isOpen}
        onClose={() =>
          setDeactivateDialog({ isOpen: false, dealId: "", dealName: "" })
        }
        dealId={deactivateDialog.dealId}
        dealName={deactivateDialog.dealName}
        workspaceId={workspaceId}
      />
    </div>
  );
};
